import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Spacing, Text } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import FALLBACK_DOT_COLOR from 'constants/seatColors';
import { buildZoneMap, groupSeatsByZone } from 'utils/seatPricing';

/**
 * SeatsListByZone component.
 * Renders selected seats grouped by zone with subtotals and total (listado por zona).
 *
 * @param {object} props - selectedSeats, seatPriceZones, showTotal, categoryI18nKey.
 * @param {Array<object>} props.selectedSeats - Selected seat objects (with zone and price).
 * @param {Array<object>} [props.seatPriceZones=[]] - Ordered zones from trip details response.
 * @param {boolean} [props.showTotal=true] - Whether to show the total row.
 * @param {string} [props.categoryI18nKey] - i18n key (search ns) used to format the per-group label.
 * @returns {JSX.Element} The rendered list by zone.
 */
function SeatsListByZone({
  selectedSeats,
  seatPriceZones = [],
  showTotal = true,
  categoryI18nKey,
}) {
  const { t } = useTranslation('search');
  const zoneMap = buildZoneMap({ seatPriceZones });
  const { groups, total } = groupSeatsByZone(selectedSeats || [], { zoneMap });

  if (groups.length === 0) return null;

  return (
    <Spacing size="XS" vertical>
      {groups.map(({ zoneKey, label, count, subtotal, color, rgbColor }) => {
        const dotBackground = rgbColor ? `rgb(${rgbColor})` : color || FALLBACK_DOT_COLOR;

        return (
          <div
            key={zoneKey}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                data-testid="zone-dot"
                style={{
                  width: 8,
                  height: 8,
                  minWidth: 8,
                  borderRadius: '50%',
                  backgroundColor: dotBackground,
                }}
              />
              <Text size="S" color="grayLight" weight="regular">
                {categoryI18nKey ? t(categoryI18nKey, { count }) : `${label} - ${count}`}
              </Text>
            </div>
            <LocalizedCurrency
              price={subtotal}
              currencyStyle="currency"
              size="S"
              color="grayLight"
            />
          </div>
        );
      })}
      {showTotal && (
        <Spacing size="XS" vertical alignItems="flex-end">
          <Text size="S" weight="bold">
            <LocalizedCurrency price={total} currencyStyle="currency" />
          </Text>
        </Spacing>
      )}
    </Spacing>
  );
}

SeatsListByZone.propTypes = {
  selectedSeats: PropTypes.arrayOf(
    PropTypes.shape({
      number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      seatFareId: PropTypes.string,
      seatFareDescription: PropTypes.string,
      seatFareReference: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      isPickedAsAdjacent: PropTypes.bool,
    }),
  ),
  seatPriceZones: PropTypes.arrayOf(
    PropTypes.shape({
      seatFareReference: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      rgbColor: PropTypes.string,
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  showTotal: PropTypes.bool,
  categoryI18nKey: PropTypes.string,
};

SeatsListByZone.defaultProps = {
  selectedSeats: [],
  seatPriceZones: [],
  showTotal: true,
  categoryI18nKey: undefined,
};

export default SeatsListByZone;
